import React, { useState, useEffect } from "react";
import { FiDollarSign, FiCalendar, FiAlertCircle, FiCheckCircle, FiInfo } from "react-icons/fi";
import { listPagosDetalle } from "../api/pagos";
import Loader from "./Loader";
import { formatDateReadable } from "../utils/fechas";

const PagosDetalle = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    // Paginación para Pagos Realizados
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await listPagosDetalle();
                setData(response);
            } catch (error) {
                console.error("Error loading pagos detalle:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Lógica de paginación para pagos realizados
    const pagosRealizados = data?.pagosRealizados || [];
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = pagosRealizados.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(pagosRealizados.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Cálculo de días que faltan (Hasta fin de mes como referencia)
    const calculateDaysRemaining = (fechaActualStr) => {
        if (!fechaActualStr) return 0;
        const today = new Date(fechaActualStr + "T00:00:00");
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const timeDiff = lastDayOfMonth.getTime() - today.getTime();
        return Math.ceil(timeDiff / (1000 * 3600 * 24));
    };

    const diasFaltantes = data ? calculateDaysRemaining(data.fechaActual) : 0;

    if (loading) return <div className="p-20 flex justify-center"><Loader /></div>;
    if (!data) return <div className="p-10 text-center text-slate-500">No se pudo cargar la información de pagos.</div>;

    return (
        <div className="p-6 max-w-6xl mx-auto animate-fade-in">
            {/* Header / Cliente Info */}
            <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                        <FiDollarSign className="text-indigo-600" /> Detalle de Pagos
                    </h2>
                    <p className="text-slate-500 mt-1">
                        Cliente: <span className="font-semibold text-slate-700">{data.cliente.name}</span>
                    </p>
                </div>
                <div className="px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
                    <FiCalendar className="text-slate-400" />
                    <span className="text-sm font-medium text-slate-600">Fecha Actual: {formatDateReadable(data.fechaActual)}</span>
                </div>
            </div>

            {/* Resumen y Alerta */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Card Total Pendiente */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between">
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Total Pendiente</p>
                    <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-4xl font-black text-rose-600">${data.totalPendiente}</span>
                        <span className="text-slate-400 font-medium">USD</span>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-rose-500 font-bold text-sm bg-rose-50 px-3 py-1 rounded-lg w-fit">
                        <FiAlertCircle /> {data.cantidadMesesPendientes} Meses en mora
                    </div>
                </div>

                {/* Card Mensaje de Advertencia */}
                <div className="lg:col-span-2 bg-indigo-600 p-6 rounded-2xl shadow-lg border border-indigo-500 text-white relative overflow-hidden group">
                    <div className="relative z-10">
                        <h4 className="text-xl font-bold flex items-center gap-2 mb-2">
                            <FiAlertCircle className="animate-pulse" /> ¡Atención Importante!
                        </h4>
                        <p className="text-indigo-100 text-sm leading-relaxed mb-4 max-w-md">
                            Recordatorio: Al segundo mes de atraso se <span className="text-white font-bold underline decoration-rose-400 underline-offset-4">deshabilitará la cuenta</span> automáticamente.
                        </p>
                        <div className="flex items-center gap-4">
                            <div className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-xl border border-white/30">
                                <p className="text-[10px] uppercase font-bold tracking-widest text-indigo-200">Días restantes</p>
                                <p className="text-2xl font-black">{diasFaltantes} Días</p>
                            </div>
                            <div className="text-xs text-indigo-200 italic">
                                * Regularice su situación para evitar cortes de servicio.
                            </div>
                        </div>
                    </div>
                    <FiDollarSign className="absolute -bottom-10 -right-10 text-white/10 size-64 rotate-12 pointer-events-none group-hover:scale-110 transition-transform duration-700" />
                </div>
            </div>

            {/* Pagos Pendientes (Prioridad) */}
            <div className="mb-10 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <FiCalendar className="text-rose-500" /> Meses Pendientes de Pago
                    </h3>
                </div>
                <div className="overflow-x-auto text-sm">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Año</th>
                                <th className="px-6 py-4">Mes</th>
                                <th className="px-6 py-4 text-right">Monto</th>
                                <th className="px-6 py-4 text-center">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {data.pagosPendientes.map((p, idx) => (
                                <tr key={idx} className="hover:bg-rose-50/30 transition-colors">
                                    <td className="px-6 py-4 font-medium text-slate-700">{p.anio}</td>
                                    <td className="px-6 py-4 text-slate-600">
                                        {new Date(0, p.mes - 1).toLocaleString('es', { month: 'long' }).toUpperCase()}
                                    </td>
                                    <td className="px-6 py-4 text-right text-rose-600 font-bold">${p.monto}</td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-[10px] font-bold uppercase border border-rose-200">
                                            Pendiente
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagos Realizados */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                        <FiCheckCircle className="text-emerald-500" /> Historial de Pagos Realizados
                    </h3>
                    <span className="text-xs font-semibold px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                        {pagosRealizados.length} registros
                    </span>
                </div>
                <div className="overflow-x-auto text-sm">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Fecha Pago</th>
                                <th className="px-6 py-4">Periodo</th>
                                <th className="px-6 py-4">Método</th>
                                <th className="px-6 py-4">Referencia</th>
                                <th className="px-6 py-4 text-right">Monto</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {currentItems.map((p, idx) => (
                                <tr key={idx} className="hover:bg-emerald-50/30 transition-colors">
                                    <td className="px-6 py-4 text-slate-600">{formatDateReadable(p.fecha_pago)}</td>
                                    <td className="px-6 py-4 font-medium text-slate-700">
                                        {new Date(0, p.mes - 1).toLocaleString('es', { month: 'long' })} {p.anio}
                                    </td>
                                    <td className="px-6 py-4 text-slate-600">
                                        <span className="px-2 py-1 bg-slate-100 rounded text-xs">{p.metodo_pago}</span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-400 font-mono text-xs">{p.referencia || "---"}</td>
                                    <td className="px-6 py-4 text-right text-emerald-600 font-bold">${p.price}</td>
                                </tr>
                            ))}
                            {pagosRealizados.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-10 text-center text-slate-400 italic">No hay pagos registrados.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginación Historial */}
                {totalPages > 1 && (
                    <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
                        <p className="text-xs text-slate-500">
                            Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, pagosRealizados.length)} de {pagosRealizados.length} pagos
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === 1
                                    ? 'text-slate-300 cursor-not-allowed'
                                    : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'
                                    }`}
                            >
                                Anterior
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => paginate(i + 1)}
                                    className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${currentPage === i + 1
                                        ? 'bg-indigo-600 text-white shadow-indigo-100 shadow-md'
                                        : 'text-slate-600 hover:bg-white border border-transparent hover:border-slate-200'
                                        }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages
                                    ? 'text-slate-300 cursor-not-allowed'
                                    : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'
                                    }`}
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Info Footer */}
            <div className="mt-8 bg-slate-50 rounded-2xl p-6 border border-slate-200 flex flex-col md:flex-row gap-6 items-center">
                <div className="bg-white p-4 rounded-xl shadow-sm text-indigo-600 flex-shrink-0">
                    <FiInfo size={32} />
                </div>
                <div>
                    <h5 className="font-bold text-slate-800">Sobre la deshabilitación de cuenta</h5>
                    <p className="text-slate-600 text-sm mt-1">
                        El sistema verifica automáticamente los pagos pendientes el primer día de cada mes. Si se detectan dos meses vencidos, el acceso al sistema será restringido hasta que se regularice el pago. Si tiene dudas, por favor contacte a soporte técnico.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PagosDetalle;
